/**
 * ============================================
 * DASHBOARD PAGE - Página Principal
 * ============================================
 *
 * Esta es la página de inicio del LIMS.
 * Muestra un resumen del estado actual del laboratorio.
 *
 * ACTUALIZACIÓN: Ahora usa i18next para traducciones dinámicas.
 */

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Users, ClipboardList, TestTubes, CheckCircle } from "lucide-react";

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  /**
   * Datos de ejemplo para las tarjetas de estadísticas
   * En producción, estos datos vendrán del backend via API
   * Los títulos usan claves de traducción
   */
  const estadisticas = [
    {
      tituloKey: "dashboard.patientsToday",
      valor: "24",
      descripcionKey: "dashboard.stats.patientsChange",
      icono: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    {
      tituloKey: "dashboard.pendingOrders",
      valor: "12",
      descripcionKey: "dashboard.stats.urgentOrders",
      icono: ClipboardList,
      color: "text-orange-600",
      bgColor: "bg-orange-100",
    },
    {
      tituloKey: "dashboard.samplesInProcess",
      valor: "38",
      descripcionKey: "dashboard.stats.samplesWaiting",
      icono: TestTubes,
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    {
      tituloKey: "dashboard.validatedResults",
      valor: "156",
      descripcionKey: "dashboard.stats.today",
      icono: CheckCircle,
      color: "text-green-600",
      bgColor: "bg-green-100",
    },
  ];

  /**
   * Acciones rápidas con claves de traducción
   */
  const accesosRapidos = [
    {
      textoKey: "dashboard.quickActions.newOrder",
      icono: ClipboardList,
      ruta: "/ordenes",
      state: { openNewOrder: true },
    },
    { textoKey: "dashboard.quickActions.searchPatient", icono: Users, ruta: "/pacientes" },
    { textoKey: "dashboard.quickActions.registerSample", icono: TestTubes, ruta: "/muestras" },
    {
      textoKey: "dashboard.quickActions.viewPending",
      icono: CheckCircle,
      ruta: "/ordenes",
      state: { filterStatus: "PENDIENTE" },
    },
  ];

  /**
   * Actividad reciente (ejemplo)
   */
  const actividadReciente = [
    {
      accionKey: "dashboard.activity.orderCreated",
      tiempo: t("dashboard.activity.timeAgo", { minutes: 5 }),
    },
    {
      accionKey: "dashboard.activity.sampleProcessed",
      tiempo: t("dashboard.activity.timeAgo", { minutes: 12 }),
    },
    {
      accionKey: "dashboard.activity.resultValidated",
      tiempo: t("dashboard.activity.timeAgo", { minutes: 20 }),
    },
    {
      accionKey: "dashboard.activity.patientRegistered",
      tiempo: t("dashboard.activity.timeAgo", { minutes: 35 }),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Encabezado de bienvenida */}
      <div>
        <h2 className="text-2xl font-bold tracking-tight">{t("dashboard.welcome")}</h2>
        <p className="text-muted-foreground">{t("dashboard.subtitle")}</p>
      </div>

      {/* Grid de estadísticas */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {estadisticas.map((stat) => (
          <Card key={stat.tituloKey}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t(stat.tituloKey)}
              </CardTitle>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icono className={`h-4 w-4 ${stat.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.valor}</div>
              <p className="text-xs text-muted-foreground">{t(stat.descripcionKey)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Sección de actividad y accesos rápidos */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.recentActivity")}</CardTitle>
            <CardDescription>{t("dashboard.recentActivityDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {actividadReciente.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between border-b pb-2 last:border-0"
                >
                  <span className="text-sm">{t(item.accionKey)}</span>
                  <span className="text-xs text-muted-foreground">{item.tiempo}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("dashboard.quickAccess")}</CardTitle>
            <CardDescription>{t("dashboard.quickAccessDesc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              {accesosRapidos.map((item, index) => (
                <button
                  key={index}
                  onClick={() =>
                    navigate(item.ruta, item.state ? { state: item.state } : undefined)
                  }
                  className="flex items-center gap-2 rounded-lg border p-3 text-sm hover:bg-accent transition-colors"
                >
                  <item.icono className="h-4 w-4" />
                  {t(item.textoKey)}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
